/* import __COLOCATED_TEMPLATE__ from './card.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */

import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type ContentSection from 'embercom/models/content-service/content-section';
import {
  humanReadableObjectNames,
  objectIcons,
  objectNames,
} from 'embercom/models/data/matching-system/matching-constants';
import type IntlService from 'ember-intl/services/intl';
import { capitalize } from '@ember/string';
import Admin from 'embercom/models/admin';
import type Store from '@ember-data/store';
import { action } from '@ember/object';
import type KnowledgeHubDrawerEditorService from 'embercom/services/knowledge-hub-drawer-editor-service';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

interface Args {
  section: ContentSection;
}

export default class SideBySideReviewCardComponent extends Component<Args> {
  @service declare intl: IntlService;
  @service declare store: Store;
  @service declare knowledgeHubDrawerEditorService: KnowledgeHubDrawerEditorService;

  get section() {
    return this.args.section;
  }

  get content() {
    return this.section.relatedContent;
  }

  get updatedAt() {
    if (!this.content) {
      return '';
    }
    return this.content.updatedAt;
  }

  get icon(): InterfaceIconName {
    let entityType = this.section.entityType;
    if (!entityType) {
      return 'note' as InterfaceIconName;
    }

    return objectIcons[this.section.entityType];
  }

  get contentTypeName() {
    return capitalize(humanReadableObjectNames[this.section.entityType]);
  }

  get lastUpdatedBy() {
    let adminId = this.section.relatedContent?.updaterId;

    if (!adminId) {
      return undefined;
    }

    return Admin.peekAndMaybeLoad(this.store, adminId.toString());
  }

  get isOutdated() {
    let liveVersionId = this.section.relatedContent?.liveVersionId;
    if (!liveVersionId) {
      return false;
    }

    let originalVersionId = this.section.entityVersionId;
    return Number(liveVersionId) !== Number(originalVersionId);
  }

  @action viewContent() {
    if (!this.content) {
      return;
    }

    if (this.content.isEditable) {
      this.knowledgeHubDrawerEditorService.openEditDrawer({
        activeContentId: this.section.entityId,
        activeContentType: objectNames[this.section.entityType],
        routeName: 'apps.app.knowledge-hub.all-content',
        newTab: true,
      });
    } else {
      this.knowledgeHubDrawerEditorService.openViewDrawer({
        activeContentId: this.section.entityId,
        activeContentType: objectNames[this.section.entityType],
        routeName: 'apps.app.knowledge-hub.all-content',
        newTab: true,
      });
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'KnowledgeHub::ContentEditor::SideBySideReview::Card': typeof SideBySideReviewCardComponent;
    'knowledge-hub/content-editor/side-by-side-review/card': typeof SideBySideReviewCardComponent;
  }
}
